import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import MobiusPage from "@insite/mobius/Page";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isRedirectFromEbsSession, setRedirectFromEbsSession } from "@WhiteCap/Common/Utillities/EbsRedirectHelper";
import OsnAccountNumberModal from "@WhiteCap/Components/OSNSubscriptions/OsnAccountNumberModal";
import loadSubscriptionsData from "@WhiteCap/Handlers/SubscriptionsDashboard/GetSubscriptionsData";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import isEmpty from "lodash/isEmpty";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    loadSubscriptionsData,
};

const mapStateToProps = (state: CustomApplicationState) => {
    const session = state.context.session;

    return {
        isLoading: state.WhiteCap.subscriptionsDashboardData.isLoading,
        data: state.WhiteCap.subscriptionsDashboardData.accountLevelSubscriptions,
        session,
        userRoles: state.context.session?.userRoles || "",
        userName: state.context.session?.userName,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest,
        location: getLocation(state),
        currentSubscriptionCustomerId: state.WhiteCap.subscriptionsDashboardData.currentSubscriptionCustomerId,
        showAccountSelectionModal: state.WhiteCap.subscriptionsDashboardData.showAccountSelectionModal,
        allowDomainSetting: state.context.settings?.properties?.allowedDomain ?? "",
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

export interface SubscriptionsDashboardPageStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    errorMessage?: TypographyProps;
}

export const subscriptionsDashboardPageStyles: SubscriptionsDashboardPageStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    errorMessage: {
        variant: "h2",
        weight: 500,
        css: css`
            margin-top: 40px;
            text-align: center;
        `,
    },
};

const styles = subscriptionsDashboardPageStyles;

const Page = ({
    id,
    loadSubscriptionsData,
    isLoading,
    data,
    session,
    userRoles,
    userName,
    currentUserIsGuest,
    history,
    signInPageUrl,
    isAuthenticated,
    location,
    currentSubscriptionCustomerId,
    showAccountSelectionModal,
    allowDomainSetting,
}: Props) => {
    const { isInShell } = useShellContext();
    const dispatch = useDispatch();
    const returnUrl = location.pathname + location.search;
    if (!isAuthenticated && !isInShell && signInPageUrl) {
        history.push(`${signInPageUrl}?returnUrl=${returnUrl}`);
    }

    const billToId = session.billToId;
    const shipToId = session.shipToId;
    const [error, setError] = useState("");
    const hasConfiguredModalsRef = useRef(false);
    const normalizedUserName = (session.userName || "").toLowerCase();
    const userDomain = normalizedUserName.includes("@") ? normalizedUserName.split("@")[1] : "";
    const allowDomainList = (allowDomainSetting || "")
        .split(",")
        .map(domain => domain.trim().toLowerCase())
        .filter(Boolean);
    const isAllowedDomainUser =
        allowDomainList.length > 0 && !!userDomain && allowDomainList.some(domain => userDomain.endsWith(domain));
    const normalizedPath = (location.pathname || "").toLowerCase();
    const isOrderSubscriptionsPage = normalizedPath.includes("/myaccount/ordersubscriptions");
    const searchParams = new URLSearchParams(location.search || "");
    const isEbsSource = (searchParams.get("source") || "").toLowerCase() === "ebs";
    const redirectedFromEbsSession = isRedirectFromEbsSession();
    const shouldShowAccountSelectionModal =
        isAllowedDomainUser && isOrderSubscriptionsPage && (isEbsSource || redirectedFromEbsSession);

    if ((!billToId || !shipToId) && !isInShell) {
        return null;
    }
    useEffect(() => {
        if (isAllowedDomainUser && isOrderSubscriptionsPage && isEbsSource) {
            setRedirectFromEbsSession(true);
        }
    }, [isAllowedDomainUser, isOrderSubscriptionsPage, isEbsSource]);

    useEffect(() => {
        if (!billToId || !shipToId) {
            return;
        }

        if (shouldShowAccountSelectionModal && (showAccountSelectionModal || !currentSubscriptionCustomerId)) {
            return;
        }

        loadSubscriptionsData({
            customerId: currentSubscriptionCustomerId || "",
            currentSessionShipToId: billToId || "",
            currentSessionBillToId: shipToId || "",
            onSuccess: () => {},
            onError: (message: string) => {
                setError(message);
            },
        });
    }, [
        billToId,
        shipToId,
        currentSubscriptionCustomerId,
        shouldShowAccountSelectionModal,
        showAccountSelectionModal,
        loadSubscriptionsData,
    ]);

    useEffect(() => {
        if (hasConfiguredModalsRef.current) {
            return;
        }

        if (shouldShowAccountSelectionModal) {
            dispatch({
                type: "Custom/SubscriptionDashboard/SetCurrentSubscriptionCustomerId",
                value: "",
            });
            dispatch({
                type: "Custom/SubscriptionDashboard/ToggleAccountSelectionModal",
                value: true,
            });
            dispatch({
                type: "Custom/SubscriptionDashboard/ModifyOsnTypeSelectionModalOpenState",
                value: false,
            });
        } else {
            dispatch({
                type: "Custom/SubscriptionDashboard/ToggleAccountSelectionModal",
                value: false,
            });
            dispatch({
                type: "Custom/SubscriptionDashboard/ModifyOsnTypeSelectionModalOpenState",
                value: true,
            });
        }

        hasConfiguredModalsRef.current = true;
    }, [dispatch, shouldShowAccountSelectionModal]);

    useEffect(() => {
        if (showAccountSelectionModal && currentSubscriptionCustomerId) {
            dispatch({
                type: "Custom/SubscriptionDashboard/SetCurrentSubscriptionCustomerId",
                value: "",
            });
        }
    }, [showAccountSelectionModal, currentSubscriptionCustomerId, dispatch]);

    return (
        <MobiusPage>
            <OsnAccountNumberModal />
            {error ? (
                <Typography {...styles.errorMessage}>{error}</Typography>
            ) : isLoading && isEmpty(data) ? (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            ) : data.isFreshAccount ? (
                <Zone contentId={id} zoneName="freshAccountContent"></Zone>
            ) : (
                <Zone contentId={id} zoneName="Content" requireRows />
            )}
        </MobiusPage>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(Page)),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;
